import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children, currentUser }) => {
  const [theme, setThemeState] = useState('light');
  const [loading, setLoading] = useState(true);

  // Load theme from Firebase
  useEffect(() => {
    const loadTheme = async () => {
      let localTheme = 'light';
      try { localTheme = localStorage.getItem('bakery-theme') || 'light'; } catch (e) {}

      if (!currentUser) {
        setThemeState(localTheme);
        applyTheme(localTheme);
        setLoading(false);
        return;
      }

      try {
        const effectiveBakerId = currentUser.uid;
        const settingsDoc = await getDoc(doc(db, 'bakerSettings', effectiveBakerId));

        if (settingsDoc.exists()) {
          const settings = settingsDoc.data();
          const savedTheme = settings.theme || localTheme;
          setThemeState(savedTheme);
          applyTheme(savedTheme);
        } else {
          setThemeState(localTheme);
          applyTheme(localTheme);
        }
      } catch (error) {
        console.error('Error loading theme:', error);
        setThemeState(localTheme);
        applyTheme(localTheme);
      } finally {
        setLoading(false);
      }
    };

    loadTheme();
  }, [currentUser]);

  // Apply theme to document
  const applyTheme = (newTheme) => {
    const resolvedTheme = newTheme === 'auto'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : newTheme;

    document.documentElement.setAttribute('data-theme', resolvedTheme);
    document.documentElement.style.colorScheme = resolvedTheme;

    // Store in localStorage as backup
    try {
      localStorage.setItem('bakery-theme', newTheme);
    } catch (e) {}
  };

  // Set theme function
  const setTheme = async (newTheme) => {
    setThemeState(newTheme);
    applyTheme(newTheme);

    // Save to Firebase if user is logged in
    if (currentUser) {
      try {
        const effectiveBakerId = currentUser.uid;
        await updateDoc(doc(db, 'bakerSettings', effectiveBakerId), {
          theme: newTheme,
          updatedAt: new Date().toISOString()
        }).catch(async () => {
          await setDoc(doc(db, 'bakerSettings', effectiveBakerId), {
            theme: newTheme,
            updatedAt: new Date().toISOString()
          }, { merge: true });
        });
      } catch (error) {
        console.error('Error saving theme:', error);
      }
    }
  };

  // Listen for system theme changes when theme is 'auto'
  useEffect(() => {
    if (theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleChange = () => {
        applyTheme('auto');
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  const value = {
    theme,
    setTheme,
    loading
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
